/**
 * EventManager Service - Zustand Store
 * Single Responsibility: Manage form events (pub/sub pattern)
 */

import { create } from 'zustand';

type EventListener = (data?: any) => void;

export interface EventManagerStore {
  listeners: Map<string, Set<EventListener>>;

  /**
   * Subscribe to an event
   */
  on: (event: string, listener: EventListener) => void;

  /**
   * Unsubscribe from an event
   */
  off: (event: string, listener: EventListener) => void;

  /**
   * Emit an event
   */
  emit: (event: string, data?: any) => void;

  /**
   * Remove all listeners for an event
   */
  removeAllListeners: (event?: string) => void;

  /**
   * Get listener count for an event
   */
  listenerCount: (event: string) => number;
}

export const useEventManagerStore = create<EventManagerStore>((set, get) => ({
  listeners: new Map<string, Set<EventListener>>(),

  on: (event: string, listener: EventListener): void => {
    set((state: EventManagerStore) => {
      const newListeners = new Map<string, Set<EventListener>>(state.listeners);
      if (!newListeners.has(event)) {
        newListeners.set(event, new Set());
      }
      newListeners.get(event)!.add(listener);
      return { listeners: newListeners };
    });
  },

  off: (event: string, listener: EventListener): void => {
    set((state: EventManagerStore) => {
      const newListeners = new Map<string, Set<EventListener>>(state.listeners);
      if (newListeners.has(event)) {
        newListeners.get(event)!.delete(listener);
        if (newListeners.get(event)!.size === 0) {
          newListeners.delete(event);
        }
      }
      return { listeners: newListeners };
    });
  },

  emit: (event: string, data?: any): void => {
    const listeners = (get() as any).listeners as Map<string, Set<EventListener>>;
    if (listeners.has(event)) {
      listeners.get(event)!.forEach((listener) => {
        try {
          listener(data);
        } catch (error) {
          console.error(`Error in event listener for '${event}':`, error);
        }
      });
    }
  },

  removeAllListeners: (event?: string): void => {
    set((state: EventManagerStore) => {
      const newListeners = new Map<string, Set<EventListener>>(state.listeners);
      if (event) {
        newListeners.delete(event);
      } else {
        newListeners.clear();
      }
      return { listeners: newListeners };
    });
  },

  listenerCount: (event: string): number => {
    const listeners = (get() as any).listeners as Map<string, Set<EventListener>>;
    return listeners.has(event) ? listeners.get(event)!.size : 0;
  },
}));
